import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, featuredVideos } from '@/mocks/homeData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug) || categories[0];

  const categoryVideos = featuredVideos.filter((v) => {
    if (category.slug === 'dianying') return v.category === '电影';
    if (category.slug === 'dianshiju') return v.category === '电视剧';
    if (category.slug === 'zongyi') return v.category === '综艺';
    if (category.slug === 'dongman') return v.category === '动漫';
    if (category.slug === 'jilupian') return v.category === '纪录片';
    if (category.slug === 'tiyu') return v.category === '体育';
    if (category.slug === 'yinyue') return v.category === '音乐';
    return false;
  });

  const displayVideos = categoryVideos.length > 0 ? categoryVideos : featuredVideos;

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 芒果视频`,
    description: category.description,
    url: `https://www.mangoshipin.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '芒果视频',
      url: 'https://www.mangoshipin.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${category.name} - 芒果视频 | 免费高清${category.name}在线观看`}
        description={`芒果视频${category.name}频道，${category.description}。海量${category.name}资源免费高清在线观看，更新快、画质好。`}
        keywords={`${category.name},芒果视频${category.name},免费${category.name},高清${category.name},在线${category.name},芒果视频`}
        ogTitle={`${category.name} - 芒果视频 | 免费高清${category.name}在线观看`}
        ogDescription={`芒果视频${category.name}频道，${category.description}。海量${category.name}资源免费高清在线观看。`}
        schema={categorySchema}
      />
      <Navbar />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category.name },
            ]}
          />
        </div>

        <section className="pb-14 md:pb-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="mb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                {category.name}
              </h1>
              <p className="text-foreground-600 text-sm md:text-base">
                {category.description} · 共 {category.videoCount.toLocaleString()} 部资源
              </p>
            </div>

            <div className="flex gap-2 md:gap-3 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === category.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {displayVideos.map((video) => (
                <div
                  key={video.id}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.coverImage}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      title={`${video.title} - 芒果视频`}
                    />
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-md">
                      {video.duration}
                    </div>
                    <div className="absolute top-2 left-2">
                      <span className="bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-md">
                        {video.category}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-primary-600 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                        <i className="ri-play-fill text-2xl"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-1 font-heading">
                      {video.title}
                    </h3>
                    <div className="flex items-center gap-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-eye-line text-xs"></i>
                        </div>
                        {video.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-star-fill text-xs text-accent-500"></i>
                        </div>
                        {video.rating}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}